import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState(null);

  // Check if geolocation is supported
  const isGeolocationSupported = () => {
    return 'geolocation' in navigator;
  };

  // Get current location
  const getCurrentLocation = async () => {
    if (!isGeolocationSupported()) {
      toast.error('Geolocation is not supported by this browser');
      return null;
    }

    setIsLoading(true);
    
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const location = {
            latitude,
            longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date()
          };
          
          setCurrentLocation(location);
          setLocationPermission('granted');
          
          // Reverse geocode to get address
          try {
            const addressData = await reverseGeocode(latitude, longitude);
            setAddress(addressData);
            resolve({ location, address: addressData });
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
            resolve({ location, address: null });
          }
          
          setIsLoading(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationPermission('denied');
          setIsLoading(false);
          
          let errorMessage = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          
          toast.error(errorMessage);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  // Reverse geocoding using a free service
  const reverseGeocode = async (latitude, longitude) => {
    try {
      // Using OpenStreetMap Nominatim API (free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Reverse geocoding failed');
      }
      
      const data = await response.json();
      
      if (data && data.address) {
        const addr = data.address;
        return {
          formatted: data.display_name,
          houseNumber: addr.house_number || '',
          street: addr.road || addr.street || '',
          area: addr.suburb || addr.neighbourhood || addr.village || '',
          city: addr.city || addr.town || addr.municipality || '',
          state: addr.state || '',
          pincode: addr.postcode || '',
          country: addr.country || 'India',
          fullAddress: `${addr.house_number || ''} ${addr.road || addr.street || ''}, ${addr.suburb || addr.neighbourhood || ''}, ${addr.city || addr.town || ''} - ${addr.postcode || ''}`.trim()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  };

  // Request location permission
  const requestLocationPermission = async () => {
    if (!isGeolocationSupported()) {
      toast.error('Geolocation is not supported by this browser');
      return false;
    }

    try {
      const result = await getCurrentLocation();
      return result !== null;
    } catch (error) {
      return false;
    }
  };

  // Get distance between two coordinates (in km)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance;
  };

  // Check if location is within delivery area
  const isWithinDeliveryArea = (customerLat, customerLon, restaurantLat = 17.3850, restaurantLon = 78.4867) => {
    const distance = getDistance(customerLat, customerLon, restaurantLat, restaurantLon);
    const maxDeliveryDistance = 10; // 10 km radius
    return distance <= maxDeliveryDistance;
  };

  // Clear location data
  const clearLocation = () => {
    setCurrentLocation(null);
    setAddress(null);
    setLocationPermission('prompt');
  };

  // Check location permission status
  useEffect(() => {
    if (isGeolocationSupported()) {
      navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state);
      }).catch(() => {
        // Permission API not supported, keep as 'prompt'
      });
    }
  }, []);

  const value = {
    currentLocation,
    address,
    locationPermission,
    isLoading,
    isGeolocationSupported: isGeolocationSupported(),
    getCurrentLocation,
    requestLocationPermission,
    reverseGeocode,
    getDistance,
    isWithinDeliveryArea,
    clearLocation
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
